import { createFileRoute } from "@tanstack/react-router"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

export const Route = createFileRoute("/design/typography")({
  component: TypographyShowcase,
})

// Type definitions for typography styles
type SimpleStyle = {
  className: string
  name: string
  specs: string
  example?: string
}

type VariantStyle = {
  className: string
  name: string
  specs: string
}

type VariantGroup = {
  [key: string]: VariantStyle
}

// Typography style definitions based on Figma design tokens
const typographyStyles: {
  [key: string]: SimpleStyle | VariantGroup
} = {
  // Headings
  "heading/2xl": {
    className: "text-2xl font-bold leading-[30px] tracking-[-0.5px] font-[Ubuntu]",
    name: "Heading 2XL",
    specs: "24px / 30px / Bold / -0.5",
    example: "Aa",
  },
  "heading/xl": {
    className: "text-xl font-bold leading-[26px] tracking-[-0.5px] font-[Ubuntu]",
    name: "Heading XL",
    specs: "20px / 26px / Bold / -0.5",
    example: "Aa",
  },
  "heading/lg": {
    className: "text-lg font-bold leading-[24px] tracking-[-0.5px] font-[Ubuntu]",
    name: "Heading LG",
    specs: "18px / 24px / Bold / -0.5",
    example: "Aa",
  },
  "heading/md": {
    className: "text-base font-bold leading-[22px] tracking-normal font-[Ubuntu]",
    name: "Heading MD",
    specs: "16px / 22px / Bold / 0",
    example: "Aa",
  },

  // Body text
  "body/md": {
    regular: {
      className: "text-base font-normal leading-[22px] tracking-normal font-[Ubuntu]",
      name: "Body MD Regular",
      specs: "16px / 22px / Regular / 0",
    },
    medium: {
      className: "text-base font-medium leading-[22px] tracking-normal font-[Ubuntu]",
      name: "Body MD Medium",
      specs: "16px / 22px / Medium / 0",
    },
    bold: {
      className: "text-base font-bold leading-[22px] tracking-normal font-[Ubuntu]",
      name: "Body MD Bold",
      specs: "16px / 22px / Bold / 0",
    },
    italic: {
      className: "text-base font-normal italic leading-[22px] tracking-normal font-[Ubuntu]",
      name: "Body MD Italic",
      specs: "16px / 22px / Italic / 0",
    },
  },
  "body/sm": {
    regular: {
      className: "text-sm font-normal leading-[18px] tracking-normal font-[Ubuntu]",
      name: "Body SM Regular",
      specs: "14px / 18px / Regular / 0",
    },
    medium: {
      className: "text-sm font-medium leading-[18px] tracking-normal font-[Ubuntu]",
      name: "Body SM Medium",
      specs: "14px / 18px / Medium / 0",
    },
    bold: {
      className: "text-sm font-bold leading-[18px] tracking-normal font-[Ubuntu]",
      name: "Body SM Bold",
      specs: "14px / 18px / Bold / 0",
    },
    italic: {
      className: "text-sm font-normal italic leading-[18px] tracking-normal font-[Ubuntu]",
      name: "Body SM Italic",
      specs: "14px / 18px / Italic / 0",
    },
  },
  "body/xs": {
    regular: {
      className: "text-xs font-normal leading-[16px] tracking-normal font-[Ubuntu]",
      name: "Body XS Regular",
      specs: "12px / 16px / Regular / 0",
    },
    medium: {
      className: "text-xs font-medium leading-[16px] tracking-normal font-[Ubuntu]",
      name: "Body XS Medium",
      specs: "12px / 16px / Medium / 0",
    },
    italic: {
      className: "text-xs font-normal italic leading-[16px] tracking-normal font-[Ubuntu]",
      name: "Body XS Italic",
      specs: "12px / 16px / Italic / 0",
    },
  },

  // Labels
  "label/md": {
    regular: {
      className: "text-base font-normal leading-[16px] tracking-normal font-[Ubuntu]",
      name: "Label MD Regular",
      specs: "16px / 16px / Regular / 0",
    },
    medium: {
      className: "text-base font-medium leading-[16px] tracking-normal font-[Ubuntu]",
      name: "Label MD Medium",
      specs: "16px / 16px / Medium / 0",
    },
    bold: {
      className: "text-base font-bold leading-[16px] tracking-normal font-[Ubuntu]",
      name: "Label MD Bold",
      specs: "16px / 16px / Bold / 0",
    },
  },
  "label/sm": {
    regular: {
      className: "text-sm font-normal leading-[14px] tracking-normal font-[Ubuntu]",
      name: "Label SM Regular",
      specs: "14px / 14px / Regular / 0",
    },
    medium: {
      className: "text-sm font-medium leading-[14px] tracking-normal font-[Ubuntu]",
      name: "Label SM Medium",
      specs: "14px / 14px / Medium / 0",
    },
    bold: {
      className: "text-sm font-bold leading-[14px] tracking-normal font-[Ubuntu]",
      name: "Label SM Bold",
      specs: "14px / 14px / Bold / 0",
    },
  },
  "label/xs": {
    regular: {
      className: "text-xs font-normal leading-[12px] tracking-normal font-[Ubuntu]",
      name: "Label XS Regular",
      specs: "12px / 12px / Regular / 0",
    },
    medium: {
      className: "text-xs font-medium leading-[12px] tracking-normal font-[Ubuntu]",
      name: "Label XS Medium",
      specs: "12px / 12px / Medium / 0",
    },
    bold: {
      className: "text-xs font-bold leading-[12px] tracking-normal font-[Ubuntu]",
      name: "Label XS Bold",
      specs: "12px / 12px / Bold / 0",
    },
    strikethrough: {
      className: "text-xs font-normal leading-[12px] tracking-normal font-[Ubuntu] line-through",
      name: "Label XS Strikethrough",
      specs: "12px / 12px / Regular / 0 / Strikethrough",
    },
  },

  // Price styles
  "price/whole": {
    xl: {
      className: "text-[56px] font-bold leading-[48px] tracking-[-1px] font-[Ubuntu]",
      name: "Price Whole XL",
      specs: "56px / 48px / Bold / -1",
    },
    lg: {
      className: "text-[48px] font-bold leading-[40px] tracking-[-1px] font-[Ubuntu]",
      name: "Price Whole LG",
      specs: "48px / 40px / Bold / -1",
    },
    md: {
      className: "text-[40px] font-bold leading-[32px] tracking-[-1px] font-[Ubuntu]",
      name: "Price Whole MD",
      specs: "40px / 32px / Bold / -1",
    },
  },
  "price/decimal": {
    xl: {
      className: "text-[36px] font-bold leading-[36px] tracking-[-1px] font-[Ubuntu]",
      name: "Price Decimal XL",
      specs: "36px / 36px / Bold / -1",
    },
    lg: {
      className: "text-[28px] font-bold leading-[28px] tracking-[-1px] font-[Ubuntu]",
      name: "Price Decimal LG",
      specs: "28px / 28px / Bold / -1",
    },
    md: {
      className: "text-[20px] font-bold leading-[20px] tracking-[-0.5px] font-[Ubuntu]",
      name: "Price Decimal MD",
      specs: "20px / 20px / Bold / -0.5",
    },
  },
}

// Sample text for different content types
const sampleTexts = {
  heading: "The quick brown fox jumps over the lazy dog",
  body: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  label: "Form Label",
  price: "$99",
  priceDecimal: ".99",
}

function TypographyShowcase() {
  return (
    <div className="container mx-auto max-w-6xl space-y-8 p-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Typography</h1>
        <p className="text-muted-foreground">
          Font scales, text styles, and typography guidelines for the Shelfi design system
        </p>
      </div>

      {/* Headings Section */}
      <Card>
        <CardHeader>
          <CardTitle>Headings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(typographyStyles)
            .filter(([key]) => key.startsWith("heading/"))
            .map(([key, style]) => {
              const simpleStyle = style as SimpleStyle
              return (
                <div
                  key={key}
                  className="grid grid-cols-1 items-baseline gap-4 border-b pb-6 last:border-0 md:grid-cols-3"
                >
                  <div className="space-y-1">
                    <p className={simpleStyle.className}>{simpleStyle.example}</p>
                    <Badge variant="secondary" className="text-xs">
                      {simpleStyle.specs}
                    </Badge>
                  </div>
                  <div className="md:col-span-2">
                    <p className={simpleStyle.className}>{sampleTexts.heading}</p>
                    <p className="text-muted-foreground mt-1 text-xs">{simpleStyle.name}</p>
                  </div>
                </div>
              )
            })}
        </CardContent>
      </Card>

      {/* Body Text Section */}
      <Card>
        <CardHeader>
          <CardTitle>Body Text</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {Object.entries(typographyStyles)
            .filter(([key]) => key.startsWith("body/"))
            .map(([key, variants]) => {
              const variantGroup = variants as VariantGroup
              return (
                <div key={key} className="space-y-4">
                  <h3 className="text-muted-foreground text-sm font-medium uppercase">
                    {key.replace("body/", "Body ")}
                  </h3>
                  {Object.entries(variantGroup).map(([variant, style]) => (
                    <div
                      key={`${key}-${variant}`}
                      className="grid grid-cols-1 items-start gap-4 md:grid-cols-3"
                    >
                      <div className="space-y-1">
                        <p className={style.className}>Aa</p>
                        <Badge variant="secondary" className="text-xs">
                          {style.specs}
                        </Badge>
                      </div>
                      <div className="md:col-span-2">
                        <p className={style.className}>{sampleTexts.body}</p>
                        <p className="text-muted-foreground mt-1 text-xs">{style.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
        </CardContent>
      </Card>

      {/* Labels Section */}
      <Card>
        <CardHeader>
          <CardTitle>Labels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {Object.entries(typographyStyles)
            .filter(([key]) => key.startsWith("label/"))
            .map(([key, variants]) => {
              const variantGroup = variants as VariantGroup
              return (
                <div key={key} className="space-y-4">
                  <h3 className="text-muted-foreground text-sm font-medium uppercase">
                    {key.replace("label/", "Label ")}
                  </h3>
                  {Object.entries(variantGroup).map(([variant, style]) => (
                    <div
                      key={`${key}-${variant}`}
                      className="grid grid-cols-1 items-baseline gap-4 md:grid-cols-3"
                    >
                      <div className="space-y-1">
                        <p className={style.className}>Aa</p>
                        <Badge variant="secondary" className="text-xs">
                          {style.specs}
                        </Badge>
                      </div>
                      <div className="md:col-span-2">
                        <p className={style.className}>{sampleTexts.label}</p>
                        <p className="text-muted-foreground mt-1 text-xs">{style.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
        </CardContent>
      </Card>

      {/* Price Styles Section */}
      <Card>
        <CardHeader>
          <CardTitle>Price Styles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-6">
            <h3 className="text-muted-foreground text-sm font-medium uppercase">Price Whole</h3>
            {Object.entries(typographyStyles["price/whole"] as VariantGroup).map(
              ([size, style]) => {
                const decimalStyles = typographyStyles["price/decimal"] as VariantGroup
                const decimalStyle = decimalStyles[size]
                return (
                  <div
                    key={`price-whole-${size}`}
                    className="grid grid-cols-1 items-baseline gap-4 md:grid-cols-3"
                  >
                    <div className="space-y-1">
                      <p className={style.className}>{sampleTexts.price}</p>
                      <Badge variant="secondary" className="text-xs">
                        {style.specs}
                      </Badge>
                    </div>
                    <div className="md:col-span-2">
                      <div className="flex items-baseline">
                        <span className={style.className}>{sampleTexts.price}</span>
                        <span className={decimalStyle.className}>{sampleTexts.priceDecimal}</span>
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">{style.name}</p>
                    </div>
                  </div>
                )
              },
            )}
          </div>

          <div className="space-y-6">
            <h3 className="text-muted-foreground text-sm font-medium uppercase">Price Decimal</h3>
            {Object.entries(typographyStyles["price/decimal"] as VariantGroup).map(
              ([size, style]) => (
                <div
                  key={`price-decimal-${size}`}
                  className="grid grid-cols-1 items-baseline gap-4 md:grid-cols-3"
                >
                  <div className="space-y-1">
                    <p className={style.className}>{sampleTexts.priceDecimal}</p>
                    <Badge variant="secondary" className="text-xs">
                      {style.specs}
                    </Badge>
                  </div>
                  <div className="md:col-span-2">
                    <p className={style.className}>{sampleTexts.priceDecimal}</p>
                    <p className="text-muted-foreground mt-1 text-xs">{style.name}</p>
                  </div>
                </div>
              ),
            )}
          </div>
        </CardContent>
      </Card>

      {/* Usage Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guidelines</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <ul className="space-y-2">
            <li>
              <strong>Font Family:</strong> Ubuntu is the primary font family for all text across
              the application.
            </li>
            <li>
              <strong>Headings:</strong> Use heading styles for page titles, section headers, and
              important UI elements. The 2XL size is reserved for hero sections.
            </li>
            <li>
              <strong>Body Text:</strong> Use body styles for paragraphs, descriptions, and general
              content. Medium weight is ideal for emphasis within body text.
            </li>
            <li>
              <strong>Labels:</strong> Use label styles for form inputs, buttons, and UI controls.
              These have tighter line heights for compact interfaces.
            </li>
            <li>
              <strong>Price Display:</strong> Combine price whole and decimal styles for product
              pricing. The decimal portion should use the corresponding smaller size.
            </li>
            <li>
              <strong>Contrast:</strong> Ensure adequate color contrast for all text, especially for
              smaller sizes. Use neutral-700 (#505862) as the minimum for body text.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
